import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const RegulatorySubmissionPanel = ({ reportData, verificationData, className = '' }) => {
  const [submissionStatus, setSubmissionStatus] = useState('draft');
  const [selectedAuthority, setSelectedAuthority] = useState('');
  const [submissionDeadline, setSubmissionDeadline] = useState('');
  const [approvalWorkflow, setApprovalWorkflow] = useState([]);
  const [exportFormat, setExportFormat] = useState('e2b');

  const regulatoryAuthorities = [
    { value: 'ema', label: 'EMA (European Medicines Agency)', region: 'EU' },
    { value: 'fda', label: 'FDA (Food and Drug Administration)', region: 'US' },
    { value: 'mhra', label: 'MHRA (Medicines and Healthcare products Regulatory Agency)', region: 'UK' },
    { value: 'bfarm', label: 'BfArM (Federal Institute for Drugs and Medical Devices)', region: 'DE' },
    { value: 'ansm', label: 'ANSM (National Agency for Medicines and Health Products Safety)', region: 'FR' },
    { value: 'aifa', label: 'AIFA (Italian Medicines Agency)', region: 'IT' }
  ];

  const exportFormats = [
    { value: 'e2b', label: 'E2B(R3) XML', description: 'ICH standard format for regulatory submission' },
    { value: 'medwatch', label: 'MedWatch Form', description: 'FDA adverse event reporting format' },
    { value: 'cioms', label: 'CIOMS I Form', description: 'Council for International Organizations format' },
    { value: 'pdf', label: 'PDF Report', description: 'Formatted PDF document' }
  ];

  useEffect(() => {
    // Initialize approval workflow
    const mockWorkflow = [
      {
        id: 1,
        role: 'Pharmacovigilance Officer',
        name: 'Dr. Maria Konstantinou',
        status: 'completed',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        action: 'Initial Report Creation'
      },
      {
        id: 2,
        role: 'Senior Pharmacovigilance Manager',
        name: 'Dr. Andreas Papadopoulos',
        status: 'pending',
        timestamp: null,
        action: 'Medical Review & Assessment'
      },
      {
        id: 3,
        role: 'Regulatory Affairs Director',
        name: 'Dr. Elena Dimitriou',
        status: 'pending',
        timestamp: null,
        action: 'Final Approval & Submission'
      }
    ];

    setApprovalWorkflow(mockWorkflow);

    // Calculate submission deadline based on severity
    if (reportData?.severity) {
      const now = new Date();
      let deadlineDays = 15; // Default for non-serious events
      
      if (reportData?.severity === 'life_threatening' || reportData?.severity === 'fatal') {
        deadlineDays = 7; // Expedited reporting for serious events
      } else if (reportData?.severity === 'severe') {
        deadlineDays = 10;
      }
      
      const deadline = new Date(now.getTime() + deadlineDays * 24 * 60 * 60 * 1000);
      setSubmissionDeadline(deadline?.toISOString()?.split('T')?.[0]);
    }
  }, [reportData]);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'draft':
        return { color: 'text-muted-foreground', bgColor: 'bg-muted/10', icon: 'FileText', label: 'Draft' };
      case 'pending_review':
        return { color: 'text-warning', bgColor: 'bg-warning/10', icon: 'Clock', label: 'Pending Review' };
      case 'approved':
        return { color: 'text-success', bgColor: 'bg-success/10', icon: 'CheckCircle', label: 'Approved' };
      case 'submitted':
        return { color: 'text-primary', bgColor: 'bg-primary/10', icon: 'Send', label: 'Submitted' };
      case 'rejected':
        return { color: 'text-error', bgColor: 'bg-error/10', icon: 'XCircle', label: 'Rejected' };
      default:
        return { color: 'text-muted-foreground', bgColor: 'bg-muted/10', icon: 'Circle', label: 'Unknown' };
    }
  };

  const getWorkflowStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'rejected': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getWorkflowStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'pending': return 'text-warning';
      case 'rejected': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const handleSubmissionRequest = () => {
    setSubmissionStatus('pending_review');
    // Update workflow to show next step as active
    setApprovalWorkflow(prev => prev?.map(step => 
      step?.id === 2 ? { ...step, status: 'pending', timestamp: new Date() } : step
    ));
  };

  const calculateDaysUntilDeadline = () => {
    if (!submissionDeadline) return null;
    const deadline = new Date(submissionDeadline);
    const now = new Date();
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const statusConfig = getStatusConfig(submissionStatus);
  const daysUntilDeadline = calculateDaysUntilDeadline();

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center space-x-3 mb-6">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusConfig?.bgColor}`}>
          <Icon name={statusConfig?.icon} size={20} className={statusConfig?.color} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Regulatory Submission</h3>
          <p className="text-sm text-muted-foreground">EMA compliance and submission management</p>
        </div>
      </div>
      {/* Current Status */}
      <div className={`p-4 rounded-lg ${statusConfig?.bgColor} mb-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name={statusConfig?.icon} size={16} className={statusConfig?.color} />
            <span className={`font-medium ${statusConfig?.color}`}>
              {statusConfig?.label}
            </span>
          </div>
          {daysUntilDeadline !== null && (
            <span className={`text-sm px-2 py-1 rounded-full ${
              daysUntilDeadline <= 3 ? 'bg-error/10 text-error' : 
              daysUntilDeadline <= 7 ? 'bg-warning/10 text-warning': 'bg-success/10 text-success'
            }`}>
              {daysUntilDeadline > 0 ? `${daysUntilDeadline} days left` : 'Overdue'}
            </span>
          )}
        </div>
      </div>
      {/* Regulatory Authority Selection */}
      <div className="mb-6">
        <Select
          label="Regulatory Authority"
          options={regulatoryAuthorities}
          value={selectedAuthority}
          onChange={setSelectedAuthority}
          placeholder="Select regulatory authority"
          searchable
          description="Primary authority for submission"
        />
      </div>
      {/* Approval Workflow */}
      <div className="mb-6">
        <h4 className="font-medium text-foreground mb-4">Approval Workflow</h4>
        <div className="space-y-3">
          {approvalWorkflow?.map((step, index) => (
            <div key={step?.id} className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step?.status === 'completed' ? 'bg-success/10' :
                  step?.status === 'pending'? 'bg-warning/10' : 'bg-muted/10'
                }`}>
                  <Icon 
                    name={getWorkflowStatusIcon(step?.status)} 
                    size={14} 
                    className={getWorkflowStatusColor(step?.status)}
                  />
                </div>
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{step?.action}</p>
                    <p className="text-xs text-muted-foreground">
                      {step?.name} • {step?.role}
                    </p>
                  </div>
                  {step?.timestamp && (
                    <span className="text-xs text-muted-foreground">
                      {step?.timestamp?.toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  )}
                </div>
              </div>
              
              {index < approvalWorkflow?.length - 1 && (
                <div className="absolute left-4 mt-8 w-px h-6 bg-border" />
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Export Format Selection */}
      <div className="mb-6">
        <Select
          label="Export Format"
          options={exportFormats}
          value={exportFormat}
          onChange={setExportFormat}
          placeholder="Select export format"
          description="Choose format for regulatory submission"
        />
      </div>
      {/* Submission Requirements */}
      <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
          <Icon name="CheckSquare" size={16} className="text-primary" />
          <span>Submission Requirements</span>
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={14} className="text-success" />
            <span className="text-foreground">Patient information anonymized</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={14} className="text-success" />
            <span className="text-foreground">Drug details verified</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={14} className="text-success" />
            <span className="text-foreground">Adverse event classified</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name={verificationData ? "Check" : "X"} size={14} className={verificationData ? "text-success" : "text-error"} />
            <span className="text-foreground">Blockchain verification complete</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name={submissionStatus === 'approved' ? "Check" : "Clock"} size={14} className={submissionStatus === 'approved' ? "text-success" : "text-warning"} />
            <span className="text-foreground">Management approval</span>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="space-y-3">
        {submissionStatus === 'draft' && (
          <Button
            variant="default"
            onClick={handleSubmissionRequest}
            iconName="Send"
            iconPosition="left"
            fullWidth
            disabled={!verificationData}
          >
            Submit for Review
          </Button>
        )}
        
        {submissionStatus === 'approved' && (
          <Button
            variant="success"
            iconName="Upload"
            iconPosition="left"
            fullWidth
          >
            Submit to {regulatoryAuthorities?.find(auth => auth?.value === selectedAuthority)?.label || 'Regulatory Authority'}
          </Button>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
          >
            Export {exportFormat?.toUpperCase()}
          </Button>
          <Button
            variant="outline"
            iconName="Eye"
            iconPosition="left"
          >
            Preview Report
          </Button>
        </div>
      </div>
      {/* Compliance Footer */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={14} className="text-primary" />
          <span>
            Compliant with EU Regulation 2017/745 (MDR) and ICH E2A guidelines
          </span>
        </div>
      </div>
    </div>
  );
};

export default RegulatorySubmissionPanel;